import { useCustomerAvatar } from "@expert/contentful";
import { Avatar, Badge, Box, Button, Flex, FocusTrap, Group, Portal, RingProgress, Stack, Text } from "@mantine/core";
import clsx from "clsx";

import { isVoiceTask, useActiveTask, useSessionId } from "@/sdk";
import { useAgentStore } from "@/sdk/agent/store/agent.store";
import { formatPartnerName } from "@/shared-types";
import { usePreloadedImage } from "@/shared-utils";

import { usePhaseTransitionEffects } from "./usePhaseTransitionEffects";
import { useStandbyCountdown } from "./useStandbyCountdown";
import { useWarmHandoffController } from "./useWarmHandoffController";
import styles from "./WarmHandoffOverlay.module.css";
import { derivePhase } from "./warmHandoffPhase";
import { buildGreeting, getCountdownDuration, getFormattedPhone, GREETING_CLOSING } from "./warmHandoffUtils";

export function WarmHandoffOverlay() {
    const activeTask = useActiveTask();
    const phase = derivePhase(activeTask);
    const activeTaskId = activeTask?.id ?? null;

    const sessionId = useSessionId();
    const customerAvatarUrl = usePreloadedImage(useCustomerAvatar(sessionId));
    const agentName = useAgentStore((state) => state.agentName);

    const rawDurationMs = isVoiceTask(activeTask) ? activeTask.ivrInfo?.handoffRecordingRuntimeMs : undefined;
    const countdownDuration = getCountdownDuration(rawDurationMs);

    usePhaseTransitionEffects(phase, activeTaskId, rawDurationMs);
    const { isDismissed, isClosing, handleContinue } = useWarmHandoffController(phase, activeTaskId, countdownDuration);
    const ringProgress = useStandbyCountdown(phase, countdownDuration);

    if (isDismissed || phase === "hidden") return null;

    const voiceTask = isVoiceTask(activeTask) ? activeTask : null;
    const customerPhone = voiceTask?.callerId || voiceTask?.mdn || "";
    const customerCarrier = voiceTask?.partner;
    const firstName = agentName.split(/[\s.]/)[0] || "Expert";
    const expertScript = voiceTask?.ivrInfo?.expertScript;
    const greetingBefore = buildGreeting(customerCarrier, firstName);

    const isLive = phase === "live";

    const ringLabel = (
        <Group gap="xs" justify="center" className={isLive ? styles.badgeLive : styles.badgeStandby}>
            <Box className={isLive ? styles.dotLive : styles.dotStandby} />
            <Text fw={isLive ? 700 : 600} size="md">
                {isLive ? "ON AIR" : "STANDBY"}
            </Text>
        </Group>
    );

    return (
        <Portal>
            <FocusTrap active>
                <Flex
                    className={clsx(styles.overlay, isClosing && styles.overlayClosing)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Warm Handoff"
                >
                    <Box className={clsx(styles.card, isClosing && styles.cardClosing)}>
                        <Group justify="space-between" mb="xl">
                            <Group gap="sm">
                                <Box className={styles.avatarWrapper}>
                                    <Avatar size={44} radius="xl" color="gray" />
                                    {customerAvatarUrl && (
                                        <img
                                            src={customerAvatarUrl}
                                            alt=""
                                            aria-hidden="true"
                                            className={styles.avatarImage}
                                        />
                                    )}
                                </Box>
                                <Text size="lg" fw={600} c="white" className="fs-mask">
                                    {getFormattedPhone(customerPhone)}
                                </Text>
                            </Group>
                            {customerCarrier && (
                                <Badge
                                    variant="outline"
                                    color="gray.7"
                                    c="gray.3"
                                    fw={400}
                                    fz="lg"
                                    radius="xl"
                                    size="xl"
                                    tt="none"
                                    className={styles.carrierBadge}
                                >
                                    {formatPartnerName(customerCarrier)}
                                </Badge>
                            )}
                        </Group>

                        <Stack align="center" gap="lg">
                            <RingProgress
                                size={176}
                                thickness={6}
                                roundCaps
                                rootColor="var(--mantine-color-dark-4)"
                                sections={[
                                    { value: isLive ? 100 : ringProgress, color: isLive ? "teal.6" : "#f59e0b" },
                                ]}
                                classNames={{ root: styles.ringWrapper }}
                                label={ringLabel}
                            />
                            <Text size="md" c="dark.2" maw="32rem" ta="center">
                                {isLive ? "You're live — " : "You're "}
                                <Text span fw={isLive ? 600 : 700} c={isLive ? "teal.6" : undefined}>
                                    {isLive ? "the customer can hear you now." : "muted"}
                                </Text>
                                {!isLive && ". Get ready to greet the customer."}
                            </Text>
                        </Stack>

                        <Box
                            className={clsx(
                                styles.teleprompter,
                                isLive ? styles.teleprompterLive : styles.teleprompterStandby,
                            )}
                            mt="xl"
                        >
                            <Text size="xs" fw={600} tt="uppercase" lts="0.1em" c="dark.3" mb="xs">
                                Say this greeting
                            </Text>
                            <Text size="xl" fw={400} lh={1.4} c="white">
                                &ldquo;{greetingBefore}
                                {expertScript && (
                                    <>
                                        {" "}
                                        <Text span className={styles.intentHighlight}>
                                            {expertScript}
                                        </Text>
                                    </>
                                )}{" "}
                                {GREETING_CLOSING}&rdquo;
                            </Text>
                        </Box>

                        <Flex justify="flex-end" mt="xl">
                            <Button
                                disabled={!isLive}
                                onClick={handleContinue}
                                radius="xl"
                                size="lg"
                                fw={700}
                                fz="lg"
                                color="asurion"
                            >
                                Continue
                            </Button>
                        </Flex>
                    </Box>
                </Flex>
            </FocusTrap>
        </Portal>
    );
}
